function padDigits (value, width) {
  return String(value).padStart(width, '0')
}

function clampNumber (value, min, max, fallback = min) {
  const numericValue = parseInt(value, 10)
  if (!Number.isFinite(numericValue)) {
    return fallback
  }
  return Math.min(max, Math.max(min, numericValue))
}

function normalizeDateFallback (fallbackValue) {
  const fallback = String(fallbackValue || '').trim()
  const match = fallback.match(/^(-?)(\d+)\/(\d{2})\/(\d{2})$/)
  if (!match) {
    return '2000/01/01'
  }

  const prefix = match[1] || ''
  const year = match[2].replace(/^0+/, '') || '1'
  const month = padDigits(clampNumber(match[3], 1, 12, 1), 2)
  const day = padDigits(clampNumber(match[4], 1, 31, 1), 2)
  return `${prefix}${year}/${month}/${day}`
}

function normalizeTimeFallback (fallbackValue) {
  const fallback = String(fallbackValue || '').trim()
  const match = fallback.match(/^(\d{2}):(\d{2}):(\d{2})$/)
  if (!match) {
    return '00:00:00'
  }

  const hour = padDigits(clampNumber(match[1], 0, 23, 0), 2)
  const minute = padDigits(clampNumber(match[2], 0, 59, 0), 2)
  const second = padDigits(clampNumber(match[3], 0, 59, 0), 2)
  return `${hour}:${minute}:${second}`
}

function buildDateString (negative, yearDigits, monthDigits, dayDigits, shouldPad = true) {
  // 年份允许 1 到 5 位
  let year = String(yearDigits || '').replace(/\D/g, '')
  year = year.slice(0, 5)
  if (shouldPad) {
    year = year.replace(/^0+/, '')
    if (!year || year === '0') {
      year = '1'
    }
  }

  let month = String(monthDigits || '').replace(/\D/g, '').slice(0, 2)
  let day = String(dayDigits || '').replace(/\D/g, '').slice(0, 2)

  if (shouldPad) {
    month = padDigits(clampNumber(month, 1, 12, 1), 2)
    day = padDigits(clampNumber(day, 1, 31, 1), 2)
  }

  return `${negative ? '-' : ''}${year}/${month}/${day}`
}

function normalizeDateInput (rawValue, fallbackValue = '2000/01/01', shouldPad = true) {
  const fallback = normalizeDateFallback(fallbackValue)
  const source = String(rawValue == null ? '' : rawValue).trim()
  if (!source) {
    return fallback
  }

  const separatedMatch = source.match(/^(-)?(\d*)[/.-](\d{0,2})[/.-](\d{0,2})$/)
  if (separatedMatch) {
    return buildDateString(Boolean(separatedMatch[1]), separatedMatch[2], separatedMatch[3], separatedMatch[4], shouldPad)
  }

  const negative = source.startsWith('-')
  const digits = source.replace(/\D/g, '')
  const yearDigitsLength = digits.length - 4
  if (yearDigitsLength < 1) {
    return fallback
  }

  return buildDateString(
    negative,
    digits.slice(0, yearDigitsLength),
    digits.slice(yearDigitsLength, yearDigitsLength + 2),
    digits.slice(yearDigitsLength + 2, yearDigitsLength + 4),
    shouldPad
  )
}

function normalizeTimeInput (rawValue, fallbackValue = '00:00:00', shouldPad = true) {
  const fallback = normalizeTimeFallback(fallbackValue)
  const source = String(rawValue == null ? '' : rawValue).trim()
  if (!source) {
    return fallback
  }

  const separatedMatch = source.match(/^(\d{0,2}):(\d{0,2}):(\d{0,2})$/)
  if (separatedMatch) {
    const h = separatedMatch[1]
    const m = separatedMatch[2]
    const s = separatedMatch[3]
    if (shouldPad) {
      const hour = padDigits(clampNumber(h, 0, 23, 0), 2)
      const minute = padDigits(clampNumber(m, 0, 59, 0), 2)
      const second = padDigits(clampNumber(s, 0, 59, 0), 2)
      return `${hour}:${minute}:${second}`
    }
    return `${h}:${m}:${s}`
  }

  const digits = source.replace(/\D/g, '')
  if (digits.length < 6) {
    return fallback
  }

  const h = digits.slice(0, 2)
  const m = digits.slice(2, 4)
  const s = digits.slice(4, 6)
  if (shouldPad) {
    const hour = padDigits(clampNumber(h, 0, 23, 0), 2)
    const minute = padDigits(clampNumber(m, 0, 59, 0), 2)
    const second = padDigits(clampNumber(s, 0, 59, 0), 2)
    return `${hour}:${minute}:${second}`
  }
  return `${h}:${m}:${s}`
}

function normalizeByType (type, rawValue, fallbackValue, shouldPad = true) {
  if (type === 'date') {
    return normalizeDateInput(rawValue, fallbackValue, shouldPad)
  }
  return normalizeTimeInput(rawValue, fallbackValue, shouldPad)
}

function getMaskedSegmentState (type, value, shouldPad = false) {
  const normalizedValue = normalizeByType(type, value, type === 'date' ? '2000/01/01' : '00:00:00', shouldPad)
  const separator = type === 'date' ? '/' : ':'
  const isNegative = type === 'date' && normalizedValue.startsWith('-')
  const content = isNegative ? normalizedValue.slice(1) : normalizedValue
  const parts = content.split(separator)
  const ranges = []

  let currentPos = isNegative ? 1 : 0
  for (let i = 0; i < parts.length; i++) {
    const digitStart = currentPos
    const end = currentPos + parts[i].length
    ranges.push({
      start: isNegative && i === 0 ? 0 : digitStart,
      end,
      digitStart,
      digitEnd: end
    })
    currentPos = end + 1
  }

  return { normalizedValue, ranges }
}

function getNextSegmentRange (type, value, selectionStart) {
  const { normalizedValue, ranges } = getMaskedSegmentState(type, value, false)
  const currentIndex = ranges.findIndex(range => selectionStart >= range.start && selectionStart <= range.end)
  const nextIndex = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, ranges.length - 1)
  const range = ranges[nextIndex] || { start: 0, end: normalizedValue.length }
  return { start: range.start, end: range.end }
}

function getSegmentMaxWidth (type, segmentIndex) {
  if (type === 'date' && segmentIndex === 0) {
    return 5
  }
  return 2
}

function buildMaskedEditResult ({ type, currentValue, key, selectionStart = 0, selectionEnd = selectionStart, fallbackValue }) {
  const normalizedCurrentValue = normalizeByType(type, currentValue, fallbackValue, false)

  if (type === 'date' && key === '-') {
    const isNegative = normalizedCurrentValue.startsWith('-')
    const content = isNegative ? normalizedCurrentValue.slice(1) : normalizedCurrentValue
    const value = normalizeDateInput(isNegative ? content : `-${content}`, fallbackValue, false)
    const { ranges } = getMaskedSegmentState(type, value, false)
    return {
      value,
      caret: ranges[0].start,
      selectionStart: ranges[0].start,
      selectionEnd: ranges[0].end
    }
  }

  const separator = type === 'date' ? '/' : ':'
  const isDate = type === 'date'
  let isNegative = isDate && normalizedCurrentValue.startsWith('-')
  const content = isNegative ? normalizedCurrentValue.slice(1) : normalizedCurrentValue
  const parts = content.split(separator)
  const { ranges } = getMaskedSegmentState(type, normalizedCurrentValue, false)
  const matchedSegmentIndex = ranges.findIndex(range => selectionStart >= range.start && selectionStart <= range.end)
  const segmentIndex = matchedSegmentIndex === -1 ? ranges.length - 1 : matchedSegmentIndex
  const segmentRange = ranges[segmentIndex]

  if (key === separator) {
    const nextRange = getNextSegmentRange(type, normalizedCurrentValue, selectionStart)
    return {
      value: normalizedCurrentValue,
      caret: nextRange.start,
      selectionStart: nextRange.start,
      selectionEnd: nextRange.end
    }
  }

  const isDeleteKey = key === 'Backspace' || key === 'Delete'

  if (!/^\d$/.test(key) && !isDeleteKey) {
    return { value: normalizedCurrentValue, caret: selectionStart, selectionStart, selectionEnd }
  }

  let segmentValue = parts[segmentIndex] || ''
  const maxSegmentWidth = getSegmentMaxWidth(type, segmentIndex)
  const segmentStart = segmentRange.digitStart
  const segmentEnd = segmentRange.digitEnd
  const replaceStart = Math.max(selectionStart, segmentStart)
  const replaceEnd = Math.min(selectionEnd, segmentEnd)
  const offsetInSegment = Math.max(0, replaceStart - segmentStart)
  const selectionLength = Math.max(0, replaceEnd - replaceStart)

  if (isDeleteKey) {
    let signDeleted = false
    if (segmentIndex === 0 && isNegative) {
      if (selectionLength > 0) {
        if (selectionStart === 0) {
          isNegative = false
        }
      } else {
        if (key === 'Backspace' && selectionStart === 1) {
          isNegative = false
          signDeleted = true
        } else if (key === 'Delete' && selectionStart === 0) {
          isNegative = false
          signDeleted = true
        }
      }
    }

    if (!signDeleted) {
      if (selectionLength > 0) {
        segmentValue = ''
      } else {
        if (key === 'Backspace') {
          segmentValue = segmentValue.slice(0, -1)
        } else {
          return { value: normalizedCurrentValue, caret: selectionStart, selectionStart, selectionEnd }
        }
      }
    }

    parts[segmentIndex] = segmentValue
    const newValue = isDate
      ? buildDateString(isNegative, parts[0], parts[1], parts[2], false)
      : `${parts[0] || ''}:${parts[1] || ''}:${parts[2] || ''}`
    const { ranges: newRanges } = getMaskedSegmentState(type, newValue, false)
    const newSegmentRange = newRanges[segmentIndex]
    const newCaret = signDeleted ? 0 : (newSegmentRange.digitStart + segmentValue.length)
    return {
      value: newValue,
      caret: newCaret
    }
  }

  if (selectionLength > 0) {
    // Preserve a user's digit-sized selection; replacing one digit must not clear the whole segment.
    segmentValue = segmentValue.slice(0, offsetInSegment) + key + segmentValue.slice(offsetInSegment + selectionLength)
  } else {
    if (segmentValue.length >= maxSegmentWidth) {
      return { value: normalizedCurrentValue, caret: selectionStart, selectionStart, selectionEnd }
    }
    segmentValue += key
  }

  parts[segmentIndex] = segmentValue

  const newValue = isDate
    ? buildDateString(isNegative, parts[0], parts[1], parts[2], false)
    : `${parts[0] || ''}:${parts[1] || ''}:${parts[2] || ''}`
  const { ranges: newRanges } = getMaskedSegmentState(type, newValue, false)
  const newSegmentRange = newRanges[segmentIndex]
  const caretOffset = selectionLength > 0 ? offsetInSegment + 1 : segmentValue.length
  const newCaret = newSegmentRange.digitStart + Math.min(segmentValue.length, caretOffset)

  return {
    value: newValue,
    caret: newCaret
  }
}

function getSegmentRange (type, value, selectionStart) {
  const { normalizedValue, ranges } = getMaskedSegmentState(type, value, false)
  const range = ranges.find(range => selectionStart >= range.start && selectionStart <= range.end) ||
    ranges[ranges.length - 1] ||
    { start: 0, end: normalizedValue.length }
  return { start: range.start, end: range.end }
}

function getEditableCharRange (type, value, selectionStart) {
  const normalizedValue = normalizeByType(type, value, type === 'date' ? '2000/01/01' : '00:00:00', false)
  const position = Math.max(0, Math.min(selectionStart, normalizedValue.length - 1))

  if (/\d/.test(normalizedValue.charAt(position))) {
    return { start: position, end: position + 1 }
  }

  const nextSegmentRange = getSegmentRange(type, normalizedValue, position + 1)
  if (nextSegmentRange.start > position) {
    return nextSegmentRange
  }

  return getSegmentRange(type, normalizedValue, position)
}

function hasEffectiveAlarmInfo (star) {
  return Boolean(star && Array.isArray(star.alarmInfoList) && star.alarmInfoList.length > 0)
}

/**
 * ASC / DESC / MC / IC 四根轴线在报警明细（alarmInfoList）里的标识。
 *
 * 线上实测（扫描 64 张真实盘面的报警明细得到）：
 *   90000001 = ASC、90000002 = DESC、90000003 = MC、90000004 = IC
 * 名称与 ipl 同时匹配，任一命中即视为轴线报警源：ipl 是权威字段，
 * 名称兜底可防后端调整编号后静默失效。
 */
const AXIS_ALARM_SOURCE_IDS = [90000001, 90000002, 90000003, 90000004]
const AXIS_ALARM_SOURCE_NAMES = ['ASC', 'DESC', 'MC', 'IC']

/** 该条报警明细的来源是否是四轴之一 */
function isAxisAlarmSource (item) {
  if (!item) {
    return false
  }
  const sourceId = Number(item.ipl)
  if (Number.isFinite(sourceId) && AXIS_ALARM_SOURCE_IDS.indexOf(sourceId) !== -1) {
    return true
  }
  const sourceName = `${item.starName === undefined || item.starName === null ? '' : item.starName}`.trim().toUpperCase()
  return AXIS_ALARM_SOURCE_NAMES.indexOf(sourceName) !== -1
}

/** 该恒星的报警明细里是否至少有一条来自四轴 */
function hasAxisAlarmSource (star) {
  return Boolean(star) &&
    Array.isArray(star.alarmInfoList) &&
    star.alarmInfoList.some(isAxisAlarmSource)
}

/**
 * 面报警认可的「红色报警」类型。
 *
 * 线上实测共 4 档：ALARM_RED(#FF0000) / ALARM_DARK_RED(#8B0000) / ALARM_YELLOW / ALARM_GREEN。
 * 暗红同属红色报警，且实际盘面里「撞四轴」的红类报警大多是暗红（扫 24 个时辰中
 * ALARM_RED 仅 2 例），因此两者一并计入，否则绝大多数轴触发场景都点不亮红光。
 */
const RED_ALARM_TYPES = ['ALARM_RED', 'ALARM_DARK_RED']

/**
 * 推导画布报警状态。
 *
 * 口径（2026-10-08 需求调整后）：
 *   - 点报警（hasBlinkAlarm）：任意恒星报警即闪烁，与报警类型、报警源均无关；
 *   - 面报警（hasPanelAlarm）：**仅当红色报警（ALARM_RED / ALARM_DARK_RED）的报警源是
 *     ASC/DESC/MC/IC 四轴之一** 时才点亮整盘红光；红色报警若只与普通星体成相
 *     （如水星、火星），只做点报警、不点亮红光。
 *   - 其他类型（ALARM_YELLOW / ALARM_GREEN 等）保持原状：只参与点报警。
 */
function deriveAstrolabeAlarmState (starPositions) {
  const stars = Array.isArray(starPositions) ? starPositions : []
  const hasBlinkAlarm = stars.some(star => Boolean(star && star.alarm && hasEffectiveAlarmInfo(star)))
  const hasPanelAlarm = stars.some(star => Boolean(
    star &&
    star.alarm &&
    RED_ALARM_TYPES.indexOf(star.alarmType) !== -1 &&
    hasEffectiveAlarmInfo(star) &&
    hasAxisAlarmSource(star)
  ))

  return {
    hasBlinkAlarm,
    hasPanelAlarm
  }
}

module.exports = {
  normalizeDateInput,
  normalizeTimeInput,
  buildMaskedEditResult,
  getSegmentRange,
  getEditableCharRange,
  deriveAstrolabeAlarmState,
  getMaskedSegmentState
}
